import React, { useEffect, useRef } from 'react';
import { z } from 'zod';
import {
  useForm,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from './Form';
import { Input } from './Input';
import { Button } from './Button';
import { Avatar, AvatarImage, AvatarFallback } from './Avatar';
import { useUserProfile, useUpdateProfile } from '../helpers/useUserProfileApi';
import { useImageUpload } from '../helpers/useImageUpload';
import { toast } from 'sonner';
import { Skeleton } from './Skeleton';
import { UploadIcon, TrashIcon } from 'lucide-react';
import styles from './UserSettingsProfileForm.module.css';

const profileFormSchema = z.object({
  displayName: z.string().min(1, 'Display name is required'),
  // Allow empty string for removing avatar, or data URL
  avatarUrl: z.string().optional().or(z.literal('')),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

export function UserSettingsProfileForm() {
  const { data: user, isLoading, isError } = useUserProfile();
  const { mutate: updateProfile, isPending: isUpdating } = useUpdateProfile();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { isProcessing: isProcessingImage, processFile } = useImageUpload({
    maxWidth: 200,
    maxHeight: 200,
    quality: 0.8,
  });

  const form = useForm({
    defaultValues: {
      displayName: '',
      avatarUrl: '',
    },
    schema: profileFormSchema,
  });

  // Update form values when user data is loaded
  useEffect(() => {
    if (user) {
      form.setValues({
        displayName: user.displayName,
        avatarUrl: user.avatarUrl || '',
      });
    }
  }, [user, form.setValues]);

  const onSubmit = (values: ProfileFormValues) => {
    updateProfile(
      {
        displayName: values.displayName,
        avatarUrl: values.avatarUrl || null,
      },
      {
        onSuccess: () => {
          toast.success('Profile updated successfully');
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to update profile');
        },
      },
    );
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear the input value so the same file can be selected again if needed
    e.target.value = '';

    const dataUrl = await processFile(file);
    if (dataUrl) {
      form.setValues((prev) => ({ ...prev, avatarUrl: dataUrl }));
    }
  };

  const handleRemoveAvatar = () => {
    form.setValues((prev) => ({ ...prev, avatarUrl: '' }));
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Skeleton className={styles.skeletonInput} />
        <Skeleton className={styles.skeletonInput} />
        <Skeleton className={styles.skeletonButton} />
      </div>
    );
  }

  if (isError || !user) {
    return <div className={styles.error}>Failed to load profile data.</div>;
  }

  // Get initials for avatar fallback
  const initials = user.displayName
    ? user.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '??';

  const currentAvatarUrl = form.values.avatarUrl || user.avatarUrl || '';

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Profile Settings</h2>
        <p className={styles.description}>Manage your public profile information.</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.avatarSection}>
            <Avatar className={styles.avatarPreview}>
              <AvatarImage src={currentAvatarUrl} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className={styles.avatarInfo}>
              <p className={styles.avatarLabel}>Profile Picture</p>
              <div className={styles.avatarActions}>
                <input
                  type="file"
                  ref={fileInputRef}
                  className={styles.hiddenInput}
                  accept="image/*"
                  onChange={handleFileSelect}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={triggerFileInput}
                  disabled={isProcessingImage || isUpdating}
                >
                  <UploadIcon size={14} />
                  {isProcessingImage ? 'Processing...' : 'Upload Photo'}
                </Button>
                {currentAvatarUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={styles.removeButton}
                    onClick={handleRemoveAvatar}
                    disabled={isProcessingImage || isUpdating}
                  >
                    <TrashIcon size={14} />
                    Remove
                  </Button>
                )}
              </div>
              <p className={styles.avatarHelp}>
                JPG or PNG. Max file size 5MB. Image will be resized to 200x200px.
              </p>
            </div>
          </div>

          <FormItem name="email">
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input value={user.email} disabled readOnly className={styles.readOnlyInput} />
            </FormControl>
            <FormDescription>
              Your email address is managed by your account provider.
            </FormDescription>
          </FormItem>

          <FormItem name="displayName">
            <FormLabel>Display Name</FormLabel>
            <FormControl>
              <Input
                placeholder="Your Name"
                value={form.values.displayName}
                onChange={(e) =>
                  form.setValues((prev) => ({
                    ...prev,
                    displayName: e.target.value,
                  }))
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>

          {/* Hidden field for avatarUrl just to keep it in form state */}
          <input type="hidden" name="avatarUrl" value={form.values.avatarUrl} />

          <div className={styles.actions}>
            <Button type="submit" disabled={isUpdating || isProcessingImage}>
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
